const SIP_STEP_UP_SIP_INFO = {
  title: "SIP & Step-up SIP Calculator",
  intro: [
    "A SIP calculator is one of the most effective tools for helping clients understand the long-term impact of disciplined investing. While a standard SIP mutual fund calculator projects the future value of a fixed monthly investment, it does not account for a common reality: most investors' incomes grow over time. This is where a Step-up SIP can make a meaningful difference.",
    "The Wealthy SIP & Step-up SIP Calculator combines the compounding capabilities of a regular SIP along with the advantage of a step-up SIP to help Mutual Fund Distributors (MFDs) demonstrate how gradually increasing SIP contributions can significantly improve long-term wealth creation.",
    "Simply enter your client's monthly investment amount, investment tenure and expected annual return. If the client plans to increase their SIP over time, you can also add a Step-up percentage and choose whether the increase happens every six months or annually. The calculator instantly projects the estimated maturity value, total invested amount, and expected gains, making it easier to present multiple investment scenarios during client discussions.",
    "While the projections are based on expected returns and do not account for taxes, expense ratios or exit loads, the calculator serves as an effective planning and engagement tool to help clients make informed investment decisions."
  ],
  sections: [
    {
      heading: "How to Use the SIP & Step-up SIP Calculator",
      paragraphs: [
        "Using the Wealthy SIP calculator takes less than a minute.",
        "Begin by entering your client's planned monthly SIP amount. If you're helping clients determine the right contribution, the calculator also works as a practical SIP amount calculator.",
        "Next, select the investment tenure and enter the expected annual rate of return based on the recommended mutual fund portfolio.",
        "To showcase the benefits of increasing investments alongside income growth, enter a Step-up percentage and select whether the SIP increase should occur every six months or once every year. This effectively turns the tool into a Step-up SIP calculator, allowing you to compare multiple contribution strategies.",
        "Once the inputs are complete, the calculator instantly displays:"
      ],
      bullets: [
        {text: "Total invested amount"},
        {text: "Estimated capital gains"},
        {text: "Projected maturity value"}
      ],
      paragraphsAfterBullets: [
        "You can switch between graphical and tabular views to explain the investment journey more effectively. The graph visually illustrates how invested capital and returns grow over time, while the year-wise table provides a detailed breakdown of portfolio growth. If the projected corpus does not align with your client's financial goals, simply adjust the SIP amount, tenure or Step-up percentage to evaluate alternate scenarios."
      ]
    },
    {
      heading: "Benefits of Using the Wealthy SIP & Step-up SIP Calculator",
      paragraphs: [
        "The Wealthy SIP calculator is designed to help Mutual Fund Distributors simplify financial conversations, improve client engagement and present investment recommendations with greater confidence.",
        "Key benefits include:"
      ],
      bullets: [
        {text: "Projects the future value of both regular SIPs and Step-up SIPs."},
        {text: "Best for client investment projections."},
        {text: "Demonstrates how periodic SIP increases can accelerate long-term wealth creation."},
        {text: "Instantly displays total invested amount, estimated gains and projected maturity value."},
        {text: "Helps clients understand the long-term impact of compounding."},
        {text: "Supports Step-up frequencies of every six months and annually."},
        {text: "Provides interactive graphs that simplify client presentations."},
        {text: "Offers detailed year-wise investment projections for goal-based planning."},
        {text: "Allows easy comparison of different SIP amounts, expected returns and investment tenures."},
        {text: "Eliminates manual calculations, helping advisors save time while improving client experience."}
      ]
    },
    {
      heading: "What Is a Systematic Investment Plan (SIP)?",
      paragraphs: [
        "A Systematic Investment Plan (SIP) allows investors to invest a fixed amount at regular intervals, typically every month, into mutual funds. It is one of the most effective ways to build long-term wealth through disciplined investing.",
        "SIPs are driven by two key principles:"
      ],
      bullets: [
        {
          label: "Compounding",
          text: "The longer investments remain invested, the greater their potential to generate returns on previously earned returns. A SIP calculator helps demonstrate the powerful effect of compounding, making it easier for clients to appreciate the value of staying invested over the long term."
        },
        {
          label: "Rupee Cost Averaging",
          text: "Regular investments help reduce the impact of market volatility by purchasing more units when prices are lower and fewer units when prices are higher. Over time, this averages the purchase cost and encourages disciplined investing regardless of market conditions."
        }
      ],
      paragraphsAfterBullets: [
        "For Mutual Fund Distributors, SIPs are more than just an investment method – they help improve client retention, encourage long-term investing behaviour and strengthen recurring assets under management."
      ]
    },
    {
      heading: "What Is a Step-up SIP?",
      paragraphs: [
        "A Step-up SIP enables investors to periodically increase their SIP amount instead of investing the same amount throughout the investment tenure. Since incomes generally increase over time, clients can gradually invest larger amounts without significantly impacting their monthly cash flow.",
        "For example, a client starting with a monthly SIP of ₹5,000 and opting for a 10% annual Step-up would invest ₹5,500 per month in the second year, ₹6,050 in the third year, and so on. These incremental investments also benefit from compounding, helping build a significantly larger corpus over the long term.",
        "The Wealthy Step-up SIP calculator enables distributors to compare different Step-up percentages and frequencies, making it easier to demonstrate the long-term value of increasing SIP contributions as income grows.",
        "Combined with Wealthy's partner platform, distributors can seamlessly manage client portfolios, monitor investments, generate reports and deliver a superior advisory experience – all from a single platform designed specifically for Mutual Fund Distributors."
      ]
    }
  ],
  faqs: {
    heading: "FAQs",
    items: [
      {
        question: "What is a SIP & Step-up SIP Calculator?",
        answer:
          "A SIP calculator helps estimate the future value of regular monthly investments. The Wealthy calculator also functions as a Step-up SIP calculator, allowing distributors to demonstrate how increasing SIP contributions over time can impact a client's projected wealth."
      },
      {
        question: "How does a Step-up SIP Calculator work?",
        answer:
          "The Step-up SIP calculator works by considering the client's monthly SIP amount, expected annual return, investment tenure and Step-up percentage. It automatically incorporates increased future investments to estimate the projected maturity value."
      },
      {
        question: "Is a Step-up SIP better than a regular SIP?",
        answer:
          "For clients whose income is expected to grow, a Step-up SIP can potentially create a larger corpus than a fixed SIP because higher future contributions also benefit from compounding over longer investment horizons."
      },
      {
        question: "How much should clients increase their SIP every year?",
        answer:
          "Many investors choose to increase their SIP by 5%-15% annually depending on salary growth, affordability and financial goals. Even modest annual increases can significantly improve long-term outcomes, which can be easily demonstrated using the SIP calculator."
      },
      {
        question: "Can I compare different SIP scenarios using the calculator?",
        answer:
          "Yes. The Wealthy SIP calculator allows distributors to compare multiple SIP amounts, investment tenures, expected returns and Step-up percentages, helping clients choose an investment strategy aligned with their financial goals."
      }
    ]
  },
  disclaimer:
    "Disclaimer: The results generated by the calculator are indicative estimates based on the inputs provided and will be subject to change when the input values are updated. Mutual Funds do not guarantee or assure returns. Actual results may vary. The performance of a model on a sample of data used for training is not necessarily indicative of future results. The calculator does not guarantee the accuracy or completeness of the information displayed, while every effort is made to provide reliable estimates.",
  copyright:
    "© 2026 Wealthy. For educational purposes only. Not financial, legal, or regulatory advice. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully."
};

const LUMPSUM_INFO = {
  title: "Lumpsum Calculator",
  intro: [
    "A lumpsum calculator is an essential planning tool for Mutual Fund Distributors (MFDs) looking to demonstrate the long-term potential of one-time mutual fund investments. Whether your client has received a bonus, inheritance, business proceeds or has accumulated surplus savings, a lump sum calculator helps illustrate how a single investment can grow over time through the power of compounding.",
    "The Wealthy Lumpsum Calculator enables you to instantly estimate future wealth based on the investment amount, expected annual return and investment tenure. Instead of relying on manual calculations or spreadsheets, you can present clear projections that make client conversations more engaging, transparent and data-driven.",
    "While the calculator uses assumed rates of return and does not account for taxes, exit loads or fund expenses, it serves as an effective planning tool to help clients understand the potential outcomes of their investment decisions."
  ],
  sections: [
    {
      heading: "What Is a Lumpsum Investment?",
      paragraphs: [
        "A lumpsum investment refers to investing a significant amount of money in a mutual fund through a single transaction rather than investing periodically through a Systematic Investment Plan (SIP).",
        "Clients often choose a lumpsum investment when they receive a bonus, inheritance, sale proceeds from an asset, retirement benefits or have accumulated idle funds that they wish to invest for long-term wealth creation.",
        "Unlike SIPs, where investments are spread across regular intervals, a lumpsum investment begins compounding immediately, making the investment horizon and market conditions important considerations when planning client portfolios."
      ]
    },
    {
      heading: "How to Use the Lumpsum Calculator?",
      paragraphs: [
        "The Wealthy lumpsum calculator has been designed to help Mutual Fund Distributors simplify investment planning during client discussions.",
        "Simply enter three details:"
      ],
      bullets: [
        {text: "The one-time investment amount"},
        {text: "Expected annual rate of return"},
        {text: "Investment tenure"}
      ],
      paragraphsAfterBullets: [
        "The calculator instantly displays the projected maturity value of the investment.",
        "It also allows you to modify the investment amount, tenure or expected returns to compare multiple scenarios, making it easier to align investment recommendations with different financial goals such as retirement planning, children's education, wealth creation or purchasing a home.",
        "For distributors, the calculator serves as much more than a projection tool. It becomes a client engagement solution that replaces theoretical explanations with easy-to-understand visual projections.",
        "The lump sum calculator estimates the future value of an investment using the compound interest formula, and the calculation methodology remains the same regardless of who uses the calculator.",
        "The difference lies in its application.",
        "While investors may use the calculator for personal financial planning, Mutual Fund Distributors can use it to demonstrate multiple investment scenarios, support goal-based recommendations and simplify complex financial conversations using real numbers."
      ]
    },
    {
      heading: "Benefits of Using the Wealthy Lumpsum Calculator",
      paragraphs: [
        "The Wealthy Lumpsum Calculator helps Mutual Fund Distributors provide a more consultative and data-backed advisory experience."
      ],
      bullets: [
        {
          label: "Instant Investment Projections:",
          text: "Generate immediate estimates of your client's future investment value without manual calculations, improving the speed and quality of investment discussions."
        },
        {
          label: "Demonstrates the Power of Compounding:",
          text: "The lump sum calculator visually illustrates how long-term investing and compounding can significantly increase wealth over time."
        },
        {
          label: "Improves Client Engagement:",
          text: "Instead of relying on theoretical explanations, distributors can present realistic investment projections, making financial discussions more transparent and easier for clients to understand."
        },
        {
          label: "Supports Goal-Based Planning:",
          text: "Model different investment amounts and time horizons to help clients plan for retirement, children's education, wealth creation, business expansion or other long-term financial objectives."
        },
        {
          label: "Compare Multiple Scenarios:",
          text: "Adjust investment amount, expected returns or tenure to instantly compare different outcomes and recommend strategies best suited to individual client goals."
        },
        {
          label: "Saves Time:",
          text: "The calculator eliminates manual calculations and spreadsheets, allowing distributors to focus more on client relationships and advisory conversations."
        }
      ]
    },
    {
      heading: "Why Mutual Fund Distributors Should Use a Lumpsum Calculator",
      paragraphs: [
        "For Mutual Fund Distributors, investment calculators are not just planning tools – they are powerful advisory and sales enablement assets.",
        "The Wealthy lump sum calculator helps distributors:"
      ],
      bullets: [
        {text: "Build greater confidence during client meetings."},
        {text: "Present data-backed investment recommendations."},
        {text: "Compare multiple investment scenarios instantly."},
        {text: "Improve client understanding through visual projections."},
        {text: "Strengthen long-term client relationships through goal-based planning."},
        {text: "Deliver a more professional and technology-driven advisory experience."}
      ],
      paragraphsAfterBullets: [
        "Combined with Wealthy's partner platform, distributors can seamlessly manage client portfolios, monitor investments, generate reports and deliver an exceptional investment experience from a single platform built exclusively for Mutual Fund Distributors."
      ]
    }
  ],
  faqs: {
    heading: "FAQs",
    items: [
      {
        question: "What is a Lumpsum Calculator?",
        answer:
          "A lumpsum calculator is an online planning tool that estimates the future value of a one-time mutual fund investment. By entering the investment amount, expected annual return and investment tenure, distributors can help clients understand how their investment could grow over time."
      },
      {
        question: "How does a Lumpsum Calculator work?",
        answer:
          "The lump sum calculator uses the principles of compound growth to estimate the future value of a one-time investment. It considers the investment amount, expected annual return and tenure to project the estimated maturity value."
      },
      {
        question: "What is the minimum lumpsum investment in mutual funds?",
        answer:
          "The minimum investment amount varies across mutual fund schemes and fund houses. Many schemes allow investments starting from ₹100 or ₹500, while others may require higher minimum amounts. Distributors should always verify the investment requirements of the selected scheme before making recommendations."
      },
      {
        question: "Is a lumpsum investment better than a SIP?",
        answer:
          "Both approaches have their place in financial planning. A lumpsum investment may be suitable when clients have a significant amount available for immediate investment, whereas SIPs are generally preferred for disciplined investing through regular contributions. The right choice depends on the client's financial goals, cash flow, investment horizon and risk appetite."
      },
      {
        question: "Can clients redeem their lumpsum investment anytime?",
        answer:
          "Most open-ended mutual fund schemes allow investors to redeem their investments whenever required. However, certain schemes may levy an exit load if redeemed within a specified period, and capital gains taxes may apply depending on the holding period. Distributors should educate clients about these implications before investing."
      }
    ]
  },
  disclaimer:
    "Disclaimer: The results generated by the calculator are indicative estimates based on the inputs provided by the user. Actual returns may vary depending on market conditions, fund performance, taxation, expenses and other factors. The calculator is intended solely for planning and educational purposes and should not be considered investment advice.",
  copyright:
    "© 2026 Wealthy. For educational purposes only. Not financial, legal or regulatory advice. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully before investing."
};

const SWP_INFO = {
  title: "SWP Calculator",
  intro: [
    "A SWP calculator is an essential planning tool for Mutual Fund Distributors (MFDs) who help clients convert accumulated wealth into a steady and predictable income stream. While investors spend years building wealth through SIPs or lumpsum investments, a Systematic Withdrawal Plan (SWP) enables them to withdraw a fixed amount from their mutual fund investments at regular intervals without redeeming their entire portfolio at once.",
    "The Wealthy SWP Calculator allows distributors to demonstrate how different withdrawal amounts, investment tenures and expected returns impact a client's remaining corpus over time.",
    "Simply enter the investment amount, expected annual return, withdrawal amount, withdrawal frequency and investment tenure. Within seconds, the calculator estimates the total withdrawals, projected remaining corpus and expected investment growth, making retirement and income planning conversations significantly more meaningful.",
    "Whether your clients are planning retirement, seeking a secondary income stream or looking to structure withdrawals from a large mutual fund investment, the Wealthy SWP Calculator helps you present data-driven recommendations with clarity and confidence."
  ],
  sections: [
    {
      heading: "What is a Systematic Withdrawal Plan (SWP)?",
      paragraphs: [
        "A Systematic Withdrawal Plan (SWP) is a facility offered by mutual funds that allows investors to withdraw a fixed amount from their existing investment at regular intervals, such as monthly, quarterly or annually.",
        "Unlike a Systematic Investment Plan (SIP), where investors contribute money periodically into mutual funds, an SWP works in the opposite direction by generating regular cash flow from an already accumulated investment corpus.",
        "Every withdrawal is funded by redeeming a small number of mutual fund units, while the remaining investment continues to stay invested and potentially generate returns.",
        "For many investors, SWPs are commonly used for:"
      ],
      bullets: [
        {text: "Retirement income"},
        {text: "Supplementing monthly cash flow"},
        {text: "Funding recurring expenses"},
        {text: "Gradually withdrawing wealth while allowing the remaining corpus to continue growing"}
      ],
      paragraphsAfterBullets: [
        "For Mutual Fund Distributors, an SWP is not just a withdrawal facility – it is an effective retirement planning solution that helps clients convert long-term investments into sustainable income."
      ]
    },
    {
      heading: "How to Use the SWP Calculator",
      paragraphs: [
        "The Wealthy SWP Calculator has been designed to simplify withdrawal planning for both distributors and their clients.",
        "To begin, enter the following details:"
      ],
      bullets: [
        {
          label: "Total Investment Amount:",
          text: "Enter the total corpus available for systematic withdrawals. This is typically the client's existing mutual fund investment or lump sum investment."
        },
        {
          label: "Withdrawal Amount:",
          text: "Specify the amount the client wishes to withdraw at regular intervals."
        },
        {
          label: "Expected Annual Return:",
          text: "Enter the expected annual rate of return that the remaining investment corpus may generate over the investment period."
        },
        {
          label: "Investment Tenure:",
          text: "Choose the duration for which withdrawals are expected to continue."
        },
        {
          label: "Withdrawal Frequency:",
          text: "Select how frequently withdrawals should be made – monthly, quarterly or annually."
        }
      ],
      paragraphsAfterBullets: [
        "Once these details are entered, the calculator instantly displays:"
      ],
      subsections: [
        {
          bullets: [
            {text: "Total investment amount"},
            {text: "Total withdrawals during the investment period"},
            {text: "Estimated investment growth"},
            {text: "Remaining corpus at the end of the selected tenure"}
          ],
          paragraphsAfterBullets: [
            "Distributors can easily modify any of these variables to compare multiple withdrawal strategies and help clients identify the most suitable income plan.",
            "The SWP calculator estimates the future value of an investment after accounting for periodic withdrawals and expected investment returns.",
            "While the underlying mathematics involves compounding and periodic redemptions, the Wealthy SWP Calculator performs these calculations instantly, eliminating the need for manual computations or spreadsheets.",
            "For distributors, this means faster client meetings, more engaging investment discussions and greater confidence in retirement planning recommendations."
          ]
        }
      ]
    },
    {
      heading: "Benefits of Using the Wealthy SWP Calculator",
      paragraphs: [
        "The Wealthy SWP Calculator helps Mutual Fund Distributors simplify retirement planning while creating a more consultative and technology-enabled client experience."
      ],
      bullets: [
        {
          label: "Estimate Sustainable Withdrawals:",
          text: "Understand how different withdrawal amounts impact the longevity of a client's investment corpus and recommend sustainable withdrawal strategies."
        },
        {
          label: "Demonstrate Long-Term Investment Growth:",
          text: "The SWP calculator shows how the remaining investment continues to generate returns even with regular withdrawals."
        },
        {
          label: "Compare Multiple Income Strategies:",
          text: "Adjust withdrawal amounts, expected returns and investment tenure to compare different retirement income scenarios and identify the most appropriate plan."
        },
        {
          label: "Improve Client Engagement:",
          text: "Rather than relying on theoretical explanations, distributors can present visual projections that help clients understand how their investments may perform over time."
        },
        {
          label: "Support Retirement Planning:",
          text: "The calculator is particularly useful when advising retirees and clients seeking passive income from their mutual fund portfolios."
        },
        {
          label: "Save Time During Client Meetings:",
          text: "The Wealthy best SWP calculator instantly performs complex calculations, allowing distributors to focus on advisory conversations instead of manual calculations."
        },
        {
          label: "Integrates with Overall Financial Planning:",
          text: "The calculator helps distributors guide clients through both wealth creation and wealth distribution phases of their financial journey."
        }
      ]
    },
    {
      heading: "Key Factors to Consider Before Recommending an SWP",
      paragraphs: [
        "While a Systematic Withdrawal Plan (SWP) can be an effective way to generate regular income, the withdrawal strategy should always align with the client's financial goals, risk profile and investment horizon. As a Mutual Fund Distributor, discussing these factors upfront helps build trust and encourages more informed investment decisions."
      ],
      bullets: [
        {
          label: "Define the Purpose of the SWP:",
          text: "Start by understanding why the client requires regular withdrawals. The objective may be retirement income, funding recurring household expenses, supplementing salary, or meeting specific financial commitments. The withdrawal amount should be aligned with the client's actual cash flow requirements rather than an arbitrary figure."
        },
        {
          label: "Monitor Portfolio Performance:",
          text: "An SWP works best when the investment portfolio continues to generate returns over time. If withdrawals consistently exceed the portfolio's growth, the investment corpus may gradually reduce. Reviewing the portfolio periodically helps ensure the withdrawal strategy remains sustainable."
        },
        {
          label: "Select an Appropriate Withdrawal Frequency:",
          text: "Clients can choose monthly, quarterly or annual withdrawals depending on their income requirements. Retirees generally prefer monthly payouts, while other investors may opt for quarterly or annual withdrawals based on their financial planning needs."
        },
        {
          label: "Consider Inflation:",
          text: "Living expenses typically increase over time. While planning an SWP, it is important to factor in inflation and review withdrawal amounts periodically so that the client's income continues to meet future expenses."
        },
        {
          label: "Review the Strategy Regularly:",
          text: "A client's financial goals, market conditions and income requirements evolve over time. Reviewing the SWP periodically allows distributors to recommend adjustments whenever necessary and helps ensure the withdrawal strategy remains aligned with long-term objectives."
        }
      ]
    },
    {
      heading: "How SWP Works in Mutual Funds",
      paragraphs: [
        "A Systematic Withdrawal Plan (SWP) allows investors to receive a fixed amount from their mutual fund investments at regular intervals while the remaining corpus stays invested.",
        "Every time a withdrawal is processed, the mutual fund redeems the required number of units based on the prevailing Net Asset Value (NAV). The balance continues to remain invested and may potentially generate returns depending on market performance.",
        "For example, if a client has invested ₹1 crore in a mutual fund and sets up a monthly SWP of ₹50,000, the specified amount is transferred to the client's bank account every month while the remaining investment continues to participate in the market.",
        "Some important points to remember include:"
      ],
      bullets: [
        {text: "SWP withdrawals are made by redeeming mutual fund units."},
        {text: "Investors can choose the withdrawal amount and frequency."},
        {text: "Withdrawal amounts can usually be modified, paused or stopped, subject to the scheme's terms and conditions."},
        {text: "Capital gains taxation may apply depending on the type of mutual fund and holding period."},
        {text: "SWPs are commonly used for retirement planning, passive income generation and systematic wealth distribution."}
      ]
    },
    {
      heading: "SIP vs SWP",
      paragraphs: [
        "Although SIPs and SWPs are often discussed together, they serve completely different investment objectives.",
        "A Systematic Investment Plan (SIP) helps clients build wealth through disciplined, periodic investments. A Systematic Withdrawal Plan (SWP) helps clients generate regular income from the wealth they have already accumulated."
      ],
      table: {
        headers: ["Parameter", "SIP", "SWP"],
        rows: [
          ["Primary Objective", "Wealth Creation", "Regular Income Generation"],
          ["Investment Flow", "Money invested into mutual funds", "Money withdrawn from mutual funds"],
          [
            "Suitable For",
            "Salaried individuals, long-term investors",
            "Retirees, passive income seekers, investors requiring regular cash flow"
          ],
          ["Investment Stage", "Wealth Accumulation", "Wealth Distribution"],
          ["Outcome", "Builds investment corpus", "Generates systematic income"]
        ]
      },
      paragraphsAfterBullets: [
        "For distributors, both calculators complement each other.",
        "A SIP and SWP calculator enables advisors to demonstrate the complete investment lifecycle – from accumulating wealth through disciplined investing to generating sustainable income during retirement.",
        "Clients who begin their investment journey using a SIP can later transition to an SWP, making tools like the SIP with SWP calculator valuable during long-term financial planning discussions."
      ]
    },
    {
      heading: "Why Mutual Fund Distributors Should Use the Wealthy SWP Calculator",
      paragraphs: [
        "Investment calculators have become an important part of digital financial advisory.",
        "The Wealthy SWP Calculator helps Mutual Fund Distributors:"
      ],
      bullets: [
        {text: "Deliver data-backed retirement planning recommendations."},
        {text: "Improve client understanding through visual projections."},
        {text: "Compare multiple withdrawal strategies instantly."},
        {text: "Build greater confidence during client meetings."},
        {text: "Support goal-based financial planning with accurate estimates."},
        {text: "Save time by eliminating manual calculations."},
        {text: "Offer a more professional and technology-enabled advisory experience."}
      ],
      paragraphsAfterBullets: [
        "Integrated with the Wealthy Partner Platform, the calculator enables distributors to seamlessly manage client portfolios, monitor investments, generate reports and strengthen long-term client relationships, all from a single platform built exclusively for Mutual Fund Distributors."
      ]
    },
    {
      heading: "Conclusion",
      paragraphs: [
        "A Systematic Withdrawal Plan is an effective way for clients to convert long-term investments into a predictable income stream while allowing the remaining corpus to stay invested.",
        "The Wealthy SWP Calculator simplifies this process by helping Mutual Fund Distributors estimate sustainable withdrawal strategies, compare multiple scenarios and present retirement planning recommendations with confidence.",
        "Whether you're advising clients on retirement income, passive cash flow or long-term wealth distribution, the calculator enables meaningful, data-driven conversations that strengthen client trust and improve financial decision-making.",
        "As a Wealthy Partner, you also gain access to a comprehensive platform that helps simplify client management, portfolio tracking, investment execution and business growth – allowing you to deliver a superior advisory experience at scale."
      ]
    }
  ],
  faqs: {
    heading: "FAQs",
    items: [
      {
        question: "What is an SWP Calculator?",
        answer:
          "An SWP calculator is an online planning tool that estimates how regular withdrawals from a mutual fund investment may impact the remaining investment corpus over time. It helps distributors demonstrate projected withdrawals, investment growth and remaining corpus using assumed rates of return."
      },
      {
        question: "How does an SWP Calculator work?",
        answer:
          "The SWP calculator considers the initial investment amount, expected annual return, withdrawal amount, withdrawal frequency and investment tenure to estimate how the investment may perform over time while systematic withdrawals are being made."
      },
      {
        question: "Can clients choose the withdrawal amount?",
        answer:
          "Yes. Investors can generally decide both the withdrawal amount and the withdrawal frequency, subject to the terms of the mutual fund scheme. These settings can usually be modified whenever financial requirements change."
      },
      {
        question: "Is an SWP suitable only for retirees?",
        answer:
          "No. While SWPs are commonly used for retirement planning, they can also be suitable for investors seeking regular income, managing recurring expenses or gradually withdrawing accumulated wealth."
      },
      {
        question: "What happens if market returns are lower than expected?",
        answer:
          "If investment returns remain below the withdrawal rate for an extended period, the investment corpus may reduce faster than anticipated. Regular portfolio reviews help distributors recommend suitable adjustments whenever required."
      },
      {
        question: "Can I compare SIP and SWP strategies using Wealthy's calculators?",
        answer:
          "Yes. Wealthy's SIP and SWP calculator ecosystem allows distributors to demonstrate both wealth accumulation and systematic withdrawal strategies, helping clients understand the complete investment lifecycle and make informed financial decisions."
      }
    ]
  },
  disclaimer:
    "Disclaimer: The results generated by the SWP Calculator are indicative estimates based on the assumptions and inputs provided by the user. Actual returns may vary depending on market performance, fund performance, withdrawal patterns, taxation, expenses and other factors. The calculator is intended solely for planning and educational purposes and should not be considered investment advice.",
  copyright:
    "© 2026 Wealthy. For educational purposes only. Not financial, legal or regulatory advice. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully before investing."
};

const SIP_SWP_INFO = {
  title: "SIP + SWP Calculator",
  intro: [
    "Financial planning doesn't end once a client builds wealth - it evolves into ensuring that wealth can generate a reliable income throughout retirement. Helping clients understand both phases is one of the most valuable services a Mutual Fund Distributor can provide. That's where the Wealthy SIP + SWP Calculator becomes an indispensable advisory tool.",
    "Unlike a standalone SIP calculator or SWP calculator, this integrated solution combines wealth accumulation and retirement income planning into a single, easy-to-use experience. During the accumulation phase, it projects how monthly SIPs, annual Step-ups and optional lump sum investments can help build a retirement corpus. Once the client reaches retirement, the calculator transitions into the withdrawal phase, estimating how long that corpus can sustain regular withdrawals through a Systematic Withdrawal Plan (SWP).",
    "The Wealthy calculator allows Mutual Fund Distributors (MFDs) to present comprehensive retirement plans backed by clear projections instead of assumptions.",
    "By adjusting variables such as monthly SIP amount, Step-up percentage, retirement age, expected returns, withdrawal amount and withdrawal growth, distributors can compare multiple retirement scenarios and help clients make informed financial decisions with confidence."
  ],
  sections: [
    {
      heading: "What is a SIP + SWP Calculator?",
      paragraphs: [
        "A SIP + SWP Calculator is a comprehensive financial planning tool that combines two critical stages of an investor's journey – wealth creation and wealth distribution.",
        "During the first phase, the calculator estimates how regular SIP investments, annual Step-ups and optional lump sum investments can accumulate into a retirement corpus over time.",
        "During the second phase, it uses that accumulated corpus to project regular withdrawals through a Systematic Withdrawal Plan (SWP), estimating how long the investment can continue supporting the client's retirement income while the remaining corpus stays invested.",
        "Rather than using separate tools for accumulation and withdrawal planning, distributors can use a single SIP and SWP calculator to provide clients with a complete retirement roadmap – from their first investment to their retirement income.",
        "For Mutual Fund Distributors, this makes retirement planning conversations more structured, transparent and goal-oriented."
      ]
    },
    {
      heading: "How to Use the SIP + SWP Calculator?",
      paragraphs: [
        "The Wealthy SIP + SWP Calculator has been designed to make retirement planning simple for both distributors and their clients. The calculator consists of two interconnected stages."
      ],
      subsections: [
        {
          heading: "Phase 1: Wealth Accumulation (SIP Phase)",
          paragraphs: [
            "Begin by entering your client's investment details."
          ],
          bullets: [
            {
              label: "Current Age:",
              text: "Enter the client's current age to determine the available investment horizon before retirement."
            },
            {
              label: "Retirement Age:",
              text: "Select the age at which the client plans to retire and begin withdrawing income."
            },
            {
              label: "Monthly SIP Amount:",
              text: "Enter the monthly investment amount. If required, the calculator also functions as a practical SIP calculator, allowing distributors to compare different contribution levels."
            },
            {
              label: "Expected Annual Return:",
              text: "Input the expected annual rate of return based on the recommended mutual fund portfolio."
            },
            {
              label: "Annual SIP Step-up:",
              text: "Enter the percentage by which the SIP will increase every year. This allows the calculator to function as a Step-up SIP calculator, demonstrating how gradually increasing investments can significantly improve retirement outcomes."
            },
            {
              label: "Optional Lump Sum Investment:",
              text: "If the client plans to invest a one-time amount alongside regular SIPs, this can also be included to estimate a larger retirement corpus."
            }
          ],
          paragraphsAfterBullets: [
            "Once these inputs are entered, the calculator estimates the total retirement corpus that may be accumulated by the selected retirement age."
          ]
        },
        {
          heading: "Phase 2: Retirement Income (SWP Phase)",
          paragraphs: [
            "Once the accumulation phase is complete, configure the withdrawal phase."
          ],
          bullets: [
            {
              label: "SWP Start Age:",
              text: "This is generally the client's planned retirement age."
            },
            {
              label: "Monthly Withdrawal Amount:",
              text: "Enter the monthly income the client wishes to receive after retirement."
            },
            {
              label: "Annual Increase in Withdrawals:",
              text: "To account for inflation and rising living expenses, specify the expected annual increase in withdrawal amount."
            },
            {
              label: "Expected Return During Retirement:",
              text: "Enter the expected annual return that the remaining retirement corpus may continue generating during the withdrawal phase."
            }
          ]
        },
        {
          paragraphs: ["The calculator then estimates:"],
          bullets: [
            {text: "Retirement corpus at the beginning of retirement"},
            {text: "Total withdrawals during retirement"},
            {text: "Remaining investment corpus"},
            {text: "Estimated sustainability of the retirement corpus"},
            {text: "Estimated age until which withdrawals may continue"}
          ],
          paragraphsAfterBullets: [
            "Distributors can modify any of these inputs to compare multiple retirement strategies and recommend solutions aligned with each client's financial goals."
          ]
        }
      ]
    },
    {
      heading: "Benefits of Using the Wealthy SIP + SWP Calculator",
      paragraphs: [
        "The Wealthy SIP + SWP Calculator enables Mutual Fund Distributors to provide holistic retirement planning instead of isolated investment recommendations."
      ],
      bullets: [
        {
          label: "Complete Retirement Planning in One Tool:",
          text: "Unlike separate calculators, this integrated SIP and SWP calculator allows distributors to demonstrate both wealth accumulation and retirement income planning using a single platform."
        },
        {
          label: "Demonstrates the Power of Long-Term Compounding:",
          text: "The built-in SIP calculator helps clients understand how disciplined investing, annual Step-ups and time can significantly increase retirement wealth."
        },
        {
          label: "Estimates Sustainable Retirement Income:",
          text: "Using the integrated SWP calculator, distributors can estimate whether a client's retirement corpus can support their desired monthly income throughout retirement."
        },
        {
          label: "Supports Goal-Based Financial Planning:",
          text: "The calculator enables distributors to compare multiple retirement scenarios based on retirement age, investment amount, withdrawal strategy and expected market returns."
        },
        {
          label: "Simplifies Complex Financial Conversations:",
          text: "Visual charts, projections and year-wise tables help clients understand retirement planning without relying on complex financial calculations."
        },
        {
          label: "Encourages Better Client Engagement:",
          text: "Interactive scenario planning encourages meaningful discussions around retirement readiness, investment discipline and withdrawal sustainability."
        },
        {
          label: "Saves Time During Client Meetings:",
          text: "The Wealthy calculator instantly performs complex calculations, allowing distributors to focus on advisory conversations instead of manual computations."
        },
        {
          label: "Complements Other Wealth Planning Tools:",
          text: "The calculator integrates seamlessly with Wealthy's SIP calculator, SWP calculator, SIP with SWP calculator and other investment planning tools, enabling distributors to deliver a comprehensive advisory experience."
        }
      ]
    },
    {
      heading: "How SIP and SWP Work Together",
      paragraphs: [
        "A successful financial plan doesn't end with wealth creation; it also requires a strategy for generating sustainable income during retirement. This is where a Systematic Investment Plan (SIP) and a Systematic Withdrawal Plan (SWP) work together to complete the investor's financial journey.",
        "During the wealth accumulation phase, clients invest regularly through SIPs, allowing their investments to benefit from disciplined investing and the power of compounding. By gradually increasing contributions through annual Step-ups and adding occasional lump sum investments whenever possible, they can accelerate the growth of their retirement corpus.",
        "Once the client reaches retirement, the focus shifts from accumulating wealth to generating a reliable income. Instead of redeeming the entire investment at once, an SWP enables regular withdrawals while allowing the remaining corpus to stay invested and continue participating in market growth.",
        "The Wealthy SIP + SWP Calculator seamlessly connects these two stages, enabling Mutual Fund Distributors to demonstrate:"
      ],
      bullets: [
        {text: "How today's SIP decisions influence tomorrow's retirement income."},
        {text: "The impact of annual SIP Step-ups on the retirement corpus."},
        {text: "Whether the accumulated corpus can support the client's desired lifestyle after retirement."},
        {text: "How different withdrawal amounts affect the longevity of retirement savings."},
        {text: "Multiple retirement planning scenarios based on changing assumptions."}
      ],
      paragraphsAfterBullets: [
        "Instead of using separate planning tools, distributors can use a single SIP and SWP calculator to present an end-to-end retirement strategy that clients can easily understand."
      ]
    },
    {
      heading: "Why Mutual Fund Distributors Should Use the Wealthy SIP + SWP Calculator",
      paragraphs: [
        "Today's investors expect more than product recommendations – they expect holistic financial planning.",
        "The Wealthy SIP + SWP Calculator enables Mutual Fund Distributors to deliver exactly that by simplifying retirement planning and making complex financial concepts easier to explain.",
        "With the calculator, distributors can:"
      ],
      bullets: [
        {
          label: "Present Complete Retirement Plans:",
          text: "Move beyond recommending SIPs alone by demonstrating how today's investments can translate into tomorrow's retirement income."
        },
        {
          label: "Improve Client Confidence:",
          text: "Visual projections and interactive calculations help clients better understand long-term investment outcomes, making financial decisions more informed and objective."
        },
        {
          label: "Compare Multiple Retirement Scenarios:",
          text: "Instantly evaluate different combinations of SIP amounts, Step-ups, retirement ages, withdrawal amounts and expected returns to identify the most suitable retirement strategy."
        },
        {
          label: "Strengthen Advisory Conversations:",
          text: "Data-backed retirement planning builds credibility and encourages meaningful discussions around long-term financial goals rather than short-term market movements."
        },
        {
          label: "Save Time:",
          text: "The calculator eliminates manual calculations and spreadsheets, enabling distributors to spend more time understanding client goals and providing personalised advice."
        },
        {
          label: "Deliver a Technology-Driven Experience:",
          text: "Integrated with the Wealthy Partner Platform, the calculator complements portfolio management, investment execution and client servicing tools, helping distributors provide a seamless advisory experience."
        }
      ]
    },
    {
      heading: "Conclusion",
      paragraphs: [
        "Retirement planning is most effective when wealth accumulation and wealth distribution are planned together rather than in isolation.",
        "The Wealthy SIP + SWP Calculator helps Mutual Fund Distributors bridge this gap by showing clients how disciplined investing today can create sustainable income tomorrow.",
        "From estimating retirement corpus through SIPs, Step-ups and lump sum investments to projecting systematic withdrawals during retirement, the calculator provides a comprehensive financial planning experience that simplifies complex decisions and builds long-term client confidence.",
        "As a Wealthy Partner, you also gain access to a comprehensive platform designed specifically for Mutual Fund Distributors – bringing together investment execution, portfolio tracking, client servicing, reporting and digital engagement tools that help you grow your practice while delivering exceptional client experiences."
      ]
    }
  ],
  faqs: {
    heading: "FAQs",
    items: [
      {
        question: "What is a SIP + SWP Calculator?",
        answer:
          "A SIP + SWP Calculator combines a SIP calculator and an SWP calculator into a single retirement planning tool. It estimates how regular SIP investments can build a retirement corpus and projects how long that corpus may sustain systematic withdrawals after retirement."
      },
      {
        question: "How does a SIP + SWP Calculator work?",
        answer:
          "The calculator first estimates the retirement corpus using monthly SIPs, annual Step-ups and optional lump sum investments. It then uses this accumulated corpus to project regular withdrawals through an SWP while estimating the remaining corpus based on expected investment returns during retirement."
      },
      {
        question: "Can I use the SIP + SWP Calculator for retirement planning?",
        answer:
          "Yes. The calculator is specifically designed for retirement planning. It enables Mutual Fund Distributors to estimate retirement corpus, evaluate withdrawal sustainability and compare multiple retirement income strategies for their clients."
      },
      {
        question: "Are the calculator results guaranteed?",
        answer:
          "No. The calculator provides estimated projections based on the assumptions entered by the user. Actual investment returns, retirement corpus and withdrawal sustainability may differ depending on market performance, taxation, inflation, expenses and other factors. Mutual funds do not guarantee returns."
      },
      {
        question: "Why should MFDs use a SIP + SWP Calculator?",
        answer:
          "The calculator enables Mutual Fund Distributors to simplify retirement planning, compare multiple scenarios, improve client engagement and present complete financial plans using visual projections instead of manual calculations. It supports a more consultative, goal-based advisory approach."
      }
    ]
  },
  disclaimer:
    "Disclaimer: The results generated by the calculator are indicative estimates based on the user's inputs and are intended solely for planning and educational purposes. Actual investment returns, retirement corpus, withdrawal sustainability and remaining corpus may vary depending on market performance, fund returns, inflation, taxation, expenses and withdrawal patterns. Mutual fund investments do not guarantee or assure returns.",
  copyright:
    "© 2026 Wealthy. For educational purposes only. Not financial, legal or regulatory advice. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully before investing."
};

const GOAL_PLANNING_LUMPSUM_INFO = {
  title: "Goal Planning Lumpsum Calculator",
  intro: [
    "Every financial goal comes down to two things: a target amount and a timeline. Whether the plan is retirement, a child's education, buying a home, or long-term wealth creation, the question a client actually asks is usually the same: \"How much do I need to invest today to reach this?\"",
    "The Wealthy Goal Planning Lumpsum Calculator is built to answer that question directly.",
    "Most lumpsum calculators do the opposite job. They start with an investment amount and estimate what it might grow into. This one works backwards. You enter the client's target corpus, the investment period, and the expected annual return, and the calculator estimates the one-time investment needed today to get there.",
    "Behind the numbers is a straightforward compounding calculation. Interactive charts and a year-wise table make the growth path visible, which is usually easier to explain to a client than a paragraph of assumptions. Projections depend on the return you enter, so results are illustrative, but the calculator gives Mutual Fund Distributors a clean way to move goal-based conversations from rough estimates to structured planning."
  ],
  sections: [
    {
      heading: "What is a Goal Planning Lumpsum Calculator?",
      paragraphs: [
        "A Goal Planning Lumpsum Calculator is a planning tool that works out the one-time investment needed today to reach a specific financial goal in the future.",
        "The difference from a standard lumpsum calculator is the direction. A standard tool asks: \"If I invest X, what could it become?\" This one asks: \"If I need Y in the future, what should I invest today?\" The starting point is the goal, not the investment.",
        "Three inputs drive the result:"
      ],
      bullets: [
        {text: "Target corpus"},
        {text: "Investment tenure"},
        {text: "Expected annual rate of return"}
      ],
      paragraphsAfterBullets: [
        "The output is the investment required today, along with the gains expected through compounding over the period.",
        "For Mutual Fund Distributors, the value is turning a vague conversation about \"how much should I invest\" into a specific, numbers-backed answer that clients can act on."
      ]
    },
    {
      heading: "How to Use the Goal Planning Lumpsum Calculator",
      paragraphs: [
        "The Wealthy Goal Planning Lumpsum Calculator is built to make goal-based planning quick to set up and easy for clients to follow."
      ],
      subsections: [
        {
          heading: "Step 1: Enter the Target Corpus",
          paragraphs: [
            "Start with the amount your client wants to have at the end of the period. Common goals distributors plan for include:"
          ],
          bullets: [
            {text: "Retirement"},
            {text: "Children's higher education"},
            {text: "Home purchase"},
            {text: "Wealth creation"},
            {text: "Business expansion"},
            {text: "Emergency corpus"},
            {text: "Any other long-term financial objective"}
          ]
        },
        {
          heading: "Step 2: Select the Investment Tenure",
          paragraphs: [
            "Enter the number of years available to reach the goal. Tenure has a large effect on the lump sum required, because compounding does more of the work over longer periods and less over shorter ones."
          ]
        },
        {
          heading: "Step 3: Enter the Expected Annual Return",
          paragraphs: [
            "Enter the expected annual return based on the mutual fund portfolio being recommended. The calculator uses this figure to project growth over the tenure."
          ]
        },
        {
          heading: "View the Results",
          paragraphs: ["The calculator returns three numbers:"],
          bullets: [
            {text: "The one-time investment required today"},
            {text: "Estimated gains through compounding"},
            {text: "The target corpus at the end of the tenure"}
          ],
          paragraphsAfterBullets: [
            "Switch between the chart and the year-wise table to walk clients through the growth path, and change any input to compare planning scenarios side by side."
          ]
        }
      ]
    },
    {
      heading: "How Does the Goal Planning Lumpsum Calculator Work?",
      paragraphs: [
        "The Wealthy lump sum mutual fund calculator uses the standard relationship between present value and future value.",
        "Rather than starting with an investment and projecting forward, it starts with the goal and works back to the investment needed today. Given a target corpus and the years available, it applies the expected annual return to calculate the present investment that would compound to that target.",
        "This reverse approach usually lands better in client conversations, because the answer speaks directly to the action they need to take now, rather than describing an outcome they might reach later."
      ]
    },
    {
      heading: "Benefits of Using the Wealthy Goal Planning Lumpsum Calculator",
      paragraphs: [
        "The calculator makes goal-based planning easier to run and easier for clients to follow. A few specific things it does well:"
      ],
      bullets: [
        {
          label: "Reverse goal planning:",
          text: "Works out the investment needed today for a defined future goal, rather than projecting an existing investment forward."
        },
        {
          label: "Goal-aligned recommendations:",
          text: "Ties investment amounts to specific client objectives rather than generic \"how much can I invest\" numbers."
        },
        {
          label: "Compounding, made visible:",
          text: "The chart and year-wise table show growth over time in a way clients can actually see, which is usually more persuasive than a verbal explanation."
        },
        {
          label: "Scenario comparison:",
          text: "Adjust any input to compare plans side by side and find the version that fits the client's cash flow and risk profile."
        },
        {
          label: "Time saved on the maths:",
          text: "The manual calculation is done for you, so the meeting time goes into understanding the client's situation instead."
        }
      ]
    },
    {
      heading: "Why Goal-Based Investing Matters",
      paragraphs: [
        "Investing without a clear goal usually leads to inconsistent decisions and expectations that do not match the actual investment being made.",
        "Goal-based investing gives a client answers to five specific questions:"
      ],
      bullets: [
        {text: "How much corpus do I actually need?"},
        {text: "When do I need the money?"},
        {text: "How much should I invest today?"},
        {text: "How does the tenure change what I need to invest?"},
        {text: "What does compounding do over this period?"}
      ],
      paragraphsAfterBullets: [
        "Instead of recommending funds in isolation, Mutual Fund Distributors can use goal planning to build a specific roadmap for each client goal. That shift, from product suggestion to plan, is usually what turns a first conversation into a long-term advisory relationship."
      ]
    },
    {
      heading: "Why Mutual Fund Distributors Should Use the Goal Planning Lumpsum Calculator",
      paragraphs: [
        "Clients now expect specific answers, not general recommendations. The Wealthy Goal Planning Lumpsum Calculator helps distributors give them, by supporting:"
      ],
      bullets: [
        {text: "Personalised, goal-based investment plans"},
        {text: "Data-backed recommendations with visual projections"},
        {text: "Clear illustrations of what compounding actually does with a one-time investment"},
        {text: "Fast scenario comparison during a live client conversation"},
        {text: "Better client understanding, which typically improves engagement"},
        {text: "Stronger, longer advisory relationships built on clarity"},
        {text: "Less time spent on manual calculations"}
      ],
      paragraphsAfterBullets: [
        "The calculator sits inside the Wealthy Partner Platform, alongside investment execution, portfolio management, reporting, and client servicing, so the same tool that produces the plan also supports its implementation and review."
      ]
    },
    {
      heading: "Conclusion",
      paragraphs: [
        "A well-run investment plan starts with a defined goal and a clear view of the investment needed to reach it. The Wealthy Goal Planning Lumpsum Calculator handles that first step, estimating the one-time investment required to reach a target corpus based on tenure and expected returns.",
        "For Mutual Fund Distributors, this is a way to make goal planning consultative rather than transactional. The client sees what the goal actually requires, understands how compounding works over the period, and leaves the conversation with a specific plan rather than a general recommendation.",
        "Joining the Wealthy Partner Platform gives distributors access to the calculator alongside the broader toolset for managing portfolios, executing investments, tracking client progress, and growing the practice."
      ]
    }
  ],
  faqs: {
    heading: "FAQs",
    items: [
      {
        question: "What is a Goal Planning Lumpsum Calculator?",
        answer:
          "A Goal Planning Lumpsum Calculator is an online planning tool that estimates the one-time investment needed today to reach a future financial goal. It uses the target corpus, the investment tenure, and the expected annual return to arrive at the required investment amount."
      },
      {
        question: "How does a Goal Planning Lumpsum Calculator work?",
        answer:
          "The calculator works back from the goal. Given the target corpus and the number of years available, it uses the expected annual return to calculate the present investment that would compound to reach that target over the chosen period."
      },
      {
        question: "Can the Goal Planning Lumpsum Calculator be used for retirement planning?",
        answer:
          "Yes. Retirement is one of the most common uses. The same tool applies equally to other long-term goals such as a child's higher education, a home purchase, business expansion, or general wealth creation."
      },
      {
        question: "Are the calculator results guaranteed?",
        answer:
          "No. The results are indicative, based on the values entered by the user. Actual outcomes depend on market performance, taxation, expenses, and other variables. Mutual fund investments do not guarantee returns."
      },
      {
        question: "Why should Mutual Fund Distributors use this calculator?",
        answer:
          "The calculator gives distributors a fast way to run goal-based planning conversations with clients, backed by visual projections rather than back-of-envelope estimates. It supports scenario comparison in real time, keeps the meeting focused on the client's situation rather than the maths, and turns generic advice into a specific plan."
      }
    ]
  },
  disclaimer:
    "Disclaimer: The results generated by the Goal Planning Lumpsum Calculator are indicative estimates based on the values entered by the user and are intended for planning and educational purposes only. Actual outcomes may vary due to market performance, taxation, investment expenses, and other factors. Mutual funds do not guarantee or assure returns.",
  copyright:
    "© 2026 Wealthy. For educational purposes only. Not financial, legal, or regulatory advice. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully before investing."
};

const GOAL_PLANNING_SIP_LUMPSUM_INFO = {
  title: "Goal Planning SIP + Lumpsum Calculator",
  intro: [
    "Every financial goal starts with a vision – but achieving it requires a clear investment plan. Whether your clients are saving for retirement, their child's higher education, a dream home or building long-term wealth, they often ask the same question: \"How much should I invest every month to reach my goal?\"",
    "The Wealthy Goal Planning SIP + Lumpsum Calculator helps Mutual Fund Distributors answer that question with confidence.",
    "Unlike a traditional SIP calculator, which estimates the future value of existing investments, this calculator works backwards from the client's financial goal. It calculates the monthly SIP required to achieve a target corpus after considering existing lump sum investments, expected returns, annual SIP Step-ups and inflation. The Wealthy calculator enables distributors to create personalised, goal-based investment plans within minutes.",
    "Just enter the client's target corpus, investment tenure, expected annual return, SIP tenure, annual Step-up percentage, existing lump sum investment and expected inflation rate. The calculator instantly estimates the required monthly SIP, total investment, projected gains and expected corpus at maturity.",
    "Interactive graphs and year-wise tables make it easy to explain investment strategies, compare different scenarios and help clients stay committed to their financial goals."
  ],
  sections: [
    {
      heading: "What is a Goal Planning SIP + Lumpsum Calculator?",
      paragraphs: [
        "A Goal Planning SIP + Lumpsum Calculator is a comprehensive financial planning tool that estimates how much a client needs to invest regularly to achieve a specific financial objective.",
        "Instead of asking, \"How much will my investments grow?\", the calculator answers a far more relevant question:",
        "\"How much should I invest today to achieve my financial goal tomorrow?\"",
        "The calculator considers:"
      ],
      bullets: [
        {text: "Target financial goal"},
        {text: "Investment tenure"},
        {text: "Monthly SIP"},
        {text: "Existing lump sum investment"},
        {text: "Expected annual return"},
        {text: "Annual SIP Step-up"},
        {text: "Inflation"}
      ],
      paragraphsAfterBullets: [
        "Using these inputs, it calculates the monthly SIP required while also considering the future value of existing investments.",
        "For Mutual Fund Distributors, this transforms goal planning into a structured, data-driven advisory conversation instead of relying on rough estimates."
      ]
    },
    {
      heading: "How to Use the Goal Planning SIP + Lumpsum Calculator",
      paragraphs: [
        "The Wealthy Goal Planning SIP + Lumpsum Calculator is designed to simplify financial planning while helping distributors create personalised investment strategies for every client."
      ],
      subsections: [
        {
          heading: "Step 1: Enter the Target Corpus",
          paragraphs: [
            "Begin by entering the amount the client wishes to accumulate for their financial goal.",
            "This could include:"
          ],
          bullets: [
            {text: "Retirement planning"},
            {text: "Children's education"},
            {text: "Buying a home"},
            {text: "Wealth creation"},
            {text: "Business expansion"},
            {text: "Any other long-term financial objective"}
          ]
        },
        {
          heading: "Step 2: Select the Investment Tenure",
          paragraphs: [
            "Choose the number of years available to achieve the financial goal.",
            "A longer investment horizon generally allows compounding to work more effectively and may reduce the monthly SIP required."
          ]
        },
        {
          heading: "Step 3: Enter the Expected Annual Return",
          paragraphs: [
            "Input the expected annual rate of return based on the recommended mutual fund portfolio.",
            "The calculator uses this assumption to estimate future investment growth."
          ]
        },
        {
          heading: "Step 4: Add the Monthly SIP",
          paragraphs: [
            "Enter the SIP amount or allow the calculator to determine the required contribution.",
            "The calculator also functions as a practical SIP calculator, enabling distributors to compare different contribution levels."
          ]
        },
        {
          heading: "Step 5: Include Annual SIP Step-up",
          paragraphs: [
            "If the client plans to increase investments every year as income grows, enter the annual Step-up percentage.",
            "This allows the calculator to function as a step-up SIP calculator, helping clients understand how gradually increasing investments can significantly improve goal achievement."
          ]
        },
        {
          heading: "Step 6: Add Existing Lump Sum Investments",
          paragraphs: [
            "If the client has already invested a one-time amount towards the same goal, enter the investment value.",
            "The calculator estimates its future value and adjusts the required SIP accordingly, helping distributors demonstrate how existing investments contribute towards the overall goal."
          ]
        },
        {
          heading: "Step 7: Factor in Inflation",
          paragraphs: [
            "Enable the inflation option and enter the expected inflation rate.",
            "Inflation significantly impacts long-term financial goals, and incorporating it provides a more realistic estimate of the future amount required."
          ]
        },
        {
          heading: "View the Results",
          paragraphs: [
            "Once all inputs are entered, the calculator instantly displays:"
          ],
          bullets: [
            {text: "Required monthly SIP"},
            {text: "Total investment"},
            {text: "Existing lump sum contribution"},
            {text: "Estimated investment gains"},
            {text: "Projected goal corpus"}
          ],
          paragraphsAfterBullets: [
            "Distributors can switch between graphical and tabular views to explain investment progress and compare multiple planning scenarios with clients."
          ]
        }
      ]
    },
    {
      heading: "Benefits of Using the Wealthy Goal Planning SIP + Lumpsum Calculator",
      paragraphs: [
        "The Wealthy Goal Planning SIP + Lumpsum Calculator enables Mutual Fund Distributors to simplify complex financial planning while improving client engagement."
      ],
      bullets: [
        {
          label: "Reverse Goal Planning:",
          text: "Unlike a conventional SIP calculator, the tool starts with the client's financial objective and calculates the investment required to achieve it."
        },
        {
          label: "Integrates SIP and Lump Sum Investments:",
          text: "The calculator combines regular SIP investments with existing lump sum contributions, enabling more accurate financial planning."
        },
        {
          label: "Demonstrates the Power of Compounding:",
          text: "The built-in SIP calculator helps clients understand how disciplined investing and long investment horizons contribute to wealth creation."
        },
        {
          label: "Supports Annual SIP Step-ups:",
          text: "The integrated step-up SIP calculator demonstrates how increasing SIPs annually can reduce the burden of meeting long-term financial goals."
        },
        {
          label: "Inflation-Adjusted Planning:",
          text: "By accounting for inflation, distributors can present more realistic investment plans that reflect the future cost of financial goals."
        },
        {
          label: "Goal-Based Advisory:",
          text: "Instead of discussing products alone, distributors can build conversations around life goals, improving client engagement and long-term relationships."
        },
        {
          label: "Compare Multiple Investment Strategies:",
          text: "Adjust investment tenure, expected returns, SIP amounts, Step-ups and lump sum investments to instantly compare different scenarios."
        },
        {
          label: "Save Time:",
          text: "The calculator eliminates manual calculations and spreadsheets, allowing distributors to focus on understanding client aspirations and delivering personalised financial advice."
        }
      ]
    },
    {
      heading: "Why Goal-Based Investing Matters",
      paragraphs: [
        "Every meaningful financial milestone has two defining characteristics—a target amount and a timeline.",
        "Without a structured investment strategy, clients often underestimate how much they need to invest or overestimate the time available to achieve their goals.",
        "Goal-based investing addresses this challenge by converting aspirations into actionable investment plans.",
        "Instead of making random investment decisions, clients gain clarity on:"
      ],
      bullets: [
        {text: "The corpus required to achieve their goal."},
        {text: "The monthly SIP needed to reach that corpus."},
        {text: "The impact of existing investments."},
        {text: "The role of inflation in increasing future costs."},
        {text: "How annual Step-ups can accelerate wealth creation."}
      ],
      paragraphsAfterBullets: [
        "For Mutual Fund Distributors, this approach shifts conversations from selling products to solving financial problems – creating stronger relationships built on trust and long-term planning."
      ]
    },
    {
      heading: "Why Mutual Fund Distributors Should Use the Goal Planning Calculator",
      paragraphs: [
        "Modern investors increasingly seek comprehensive financial advice rather than standalone investment products.",
        "The Wealthy Goal Planning SIP + Lumpsum Calculator enables distributors to:"
      ],
      bullets: [
        {text: "Deliver personalised goal-based financial plans."},
        {text: "Present data-backed investment recommendations."},
        {text: "Demonstrate the impact of SIPs, Step-ups and lump sum investments."},
        {text: "Compare multiple planning scenarios instantly."},
        {text: "Improve client understanding through visual projections."},
        {text: "Build long-term relationships through consultative financial planning."},
        {text: "Save time by eliminating manual calculations."}
      ],
      paragraphsAfterBullets: [
        "Integrated with the Wealthy Partner Platform, the calculator complements portfolio management, client servicing, investment execution and reporting—helping distributors deliver a seamless digital advisory experience."
      ]
    },
    {
      heading: "Conclusion",
      paragraphs: [
        "Achieving financial goals begins with understanding exactly how much needs to be invested today. The Wealthy Goal Planning SIP + Lumpsum Calculator simplifies this process by combining SIP investments, annual Step-ups, existing lump sum investments and inflation into a single planning solution.",
        "For Mutual Fund Distributors, it transforms goal planning into a consultative advisory experience – making it easier to demonstrate investment strategies, compare scenarios and help clients make informed financial decisions.",
        "As a Wealthy Partner, you gain access to an integrated platform designed specifically for Mutual Fund Distributors, enabling you to manage client portfolios, execute investments, monitor progress and grow your practice with confidence."
      ]
    }
  ],
  faqs: {
    heading: "FAQs",
    items: [
      {
        question: "What is a Goal Planning SIP + Lumpsum Calculator?",
        answer:
          "A Goal Planning SIP + Lumpsum Calculator estimates the monthly SIP required to achieve a specific financial goal after considering existing lump sum investments, expected returns, annual SIP Step-ups and inflation."
      },
      {
        question: "How does the calculator work?",
        answer:
          "The calculator starts with the target corpus and works backwards to estimate the monthly SIP required. It factors in investment tenure, expected annual return, Step-up percentage, existing lump sum investments and inflation to generate personalised projections."
      },
      {
        question: "Can I include an existing lump sum investment?",
        answer:
          "Yes. The calculator estimates the future value of your existing lump sum investment and adjusts the required SIP accordingly, making the investment plan more realistic."
      },
      {
        question: "Why should inflation be included while planning financial goals?",
        answer:
          "Inflation increases the future cost of financial goals. Factoring it into the calculation helps estimate the actual corpus required and enables more accurate long-term financial planning."
      },
      {
        question: "Can I compare different investment scenarios?",
        answer:
          "Yes. You can modify the target corpus, investment tenure, expected returns, annual Step-up percentage, SIP amount and lump sum investment to compare multiple scenarios and identify the most suitable investment strategy."
      },
      {
        question: "Why should Mutual Fund Distributors use this calculator?",
        answer:
          "The calculator helps distributors simplify goal-based financial planning, improve client engagement, present visual investment projections and provide more personalised advisory services while saving valuable planning time."
      }
    ]
  },
  disclaimer:
    "Disclaimer: The results generated by the Goal Planning SIP + Lumpsum Calculator are indicative estimates based on the values entered by the user and are intended solely for planning and educational purposes. Actual investment outcomes may vary depending on market performance, inflation, taxation, expenses and other factors. Mutual funds do not guarantee or assure returns.",
  copyright:
    "© 2026 Wealthy. For educational purposes only. Not financial, legal, or regulatory advice. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully."
};

const CALCULATOR_INFO_CONTENT = {
  "sip-step-up-sip": SIP_STEP_UP_SIP_INFO,
  lumpsum: LUMPSUM_INFO,
  swp: SWP_INFO,
  "sip-swp": SIP_SWP_INFO,
  "goal-planning-lumpsum": GOAL_PLANNING_LUMPSUM_INFO,
  "goal-planning-sip-lumpsum": GOAL_PLANNING_SIP_LUMPSUM_INFO
};

export const getCalculatorInfoContent = (slug) => CALCULATOR_INFO_CONTENT[slug] || null;

export default CALCULATOR_INFO_CONTENT;
